import {
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { distinctUntilChanged, tap } from 'rxjs/operators';

import { InterceptorSkipHeader, UserRegistrationModel } from '@vi-iot/client';
import { LogType, ViLogger, ViToastService } from '@vi-iot/ui';

import { AuthStore, StatusPageStore } from '@store';

import { environment } from '../../../environments/environment';

import { OAuthService } from 'angular-oauth2-oidc';
import { viguideVersion } from '../../../../package-version';
import { DemoModeService } from './demo-mode.service';
import { TranslationConfigService } from './translation-config.service';

@Injectable()
export class InterceptorService implements HttpInterceptor {
  private toastData = {
    actions: {
      id: 'confirm-btn',
      name: 'GENERAL.API_LIMIT.BUTTON_ACCEPT',
    },
    message: 'GENERAL.API_LIMIT.MESSAGE',
  };
  private IAM_INVALID_TOKEN_RESPONSE = 'NO TOKEN AVAILABLE';

  private demo: boolean;

  constructor(
    private authStore: AuthStore,
    private toastService: ViToastService,
    private translateService: TranslateService,
    private translationConfig: TranslationConfigService,
    private viLogger: ViLogger,
    private demoModeService: DemoModeService,
    private statusPageStore: StatusPageStore,
    private oAuthService: OAuthService
  ) {
    this.initDemoSubscription();
  }
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<HttpEventType.Response>> {
    if (this.demo) {
      return this.demoModeService.interceptDemo(request, next);
    }

    if (request?.headers && request.headers.has(InterceptorSkipHeader)) {
      const newHeaders = request.headers.delete(InterceptorSkipHeader);

      return next.handle(request.clone({ headers: newHeaders }));
    }

    const requestHeaders = {};

    const headers = {
      Accept: 'application/json',
      'Accept-Language': this.locale,
      ...requestHeaders,
    };

    const iamRequest = request.url.startsWith('https://iam');
    if (!iamRequest) {
      headers['Content-Type'] = 'application/json';
      headers['viguideVersion'] = viguideVersion;
    }

    // Viguide Mobile still uses SAML - they inject csrf token into Viguide Web's local storage
    const samlToken = localStorage.getItem('csrf_token');

    if (samlToken && !request.url.endsWith('/saml/csrf')) {
      headers['Authorization'] = `CSRF ${samlToken}`;
    } else {
      const oAuthToken = this.oAuthService.getAccessToken();
      if (
        oAuthToken &&
        !request.url.endsWith('/token') &&
        !request.url.startsWith(environment.oAuthConfig.issuer)
      ) {
        headers['Authorization'] = `Bearer ${oAuthToken}`;
      }
    }

    request = request.clone({
      setHeaders: headers,
      withCredentials: false,
    });

    this.viLogger.start(request.urlWithParams, LogType.http, 'HTTP request');

    return next.handle(request).pipe(
      tap(
        (event: HttpEvent<HttpEventType.Response>) => {
          if (event instanceof HttpResponse) {
            this.viLogger.end(event.url, LogType.http, `HTTP response ${event.status}`);

            this.viLogger.log(event.body, LogType.httpResponsePayload);
          }
        },
        (res: HttpErrorResponse) => {
          if (!(res instanceof HttpErrorResponse)) {
            return;
          }

          // If an error is received from any endpoint, excluding only the 'status-app' endpoint,
          // check if status is not equal 'major_outage' and 'under_maintenance', if yes, redirect the user to the status error page
          if (
            !request.url.endsWith(`apps/${environment.statusPageAppId}/summary`) &&
            // Prevent mechanism as it's handled in auth.effects regarding action AuthActions.getMe.error
            !request.url.includes(UserRegistrationModel.meEndpointPath)
          ) {
            this.statusPageStore.getStatusWithoutTimer();
          }

          const reachedLimitOfApiCalls = res.status === 429;
          const userUnauthorized =
            res.status === 401 ||
            (res.error && res.error.error === this.IAM_INVALID_TOKEN_RESPONSE);

          if (reachedLimitOfApiCalls) {
            const toastData = {
              message: this.translateService.instant(this.toastData.message),
              actions: [
                {
                  id: this.toastData.actions.id,
                  name: this.translateService.instant(this.toastData.actions.name),
                },
              ],
            };
            this.toastService.warning(toastData, { duration: 15000 });
          } else if (
            userUnauthorized &&
            // Prevent mechanism as it's handled in auth.effects regarding action AuthActions.getMe.error
            !request.url.includes(UserRegistrationModel.meEndpointPath)
          ) {
            // if we get 401 from any endpoint then we must try to refetch CSRF TOKEN
            this.authStore.fetchCSRFTokenOn401Subject$.next();
          }
        }
      )
    );
  }

  private get locale(): string {
    return this.translationConfig.getLanguage();
  }

  private initDemoSubscription(): void {
    this.authStore.isDemo$
      .pipe(
        distinctUntilChanged(),
        tap((demo) => {
          if (demo) {
            this.demoModeService.createDemoEndpoints();
          }
        })
      )
      .subscribe((demo) => {
        this.demo = demo;
      });
  }
}
